/**
 * The site facts a tool's text may carry, and the grammar that resolves them.
 *
 * Split out of `tool-content.ts` for the same reason `interpolate.ts` is split out of
 * `lib/i18n`: that module reads the filesystem, so a client component cannot import it,
 * and the widgets are client components. A tool's prose lives in a content file and is
 * resolved by the route, but a widget's own strings live in the widget — `ui` is
 * deliberately excluded from `resolveToolFacts`, because a widget's placeholders are
 * its own business — and six widgets state the style count inside a sentence they own.
 *
 * One grammar rather than two. A second regex in `tool-ui.tsx` would be the drift the
 * rest of this codebase keeps having to fix: the day someone teaches the route to skip
 * `^{sup}` and forgets the widget, a superscript in a translated widget string becomes
 * a mangled brace token and nothing fails.
 */

/**
 * The placeholders a tool's text may carry, and the only ones.
 *
 * Deliberately a *closed* set, and deliberately one member. An unknown brace token is
 * left alone rather than rejected, because braces are ordinary content in this corpus —
 * `bibtex-key-generator` shows `@article{smith2024quantum, ...}` and `doi-to-bibtex`
 * explains BibTeX's `{DNA}` capitalisation guard — and `unit-converter` interpolates its
 * own `{category}`. Substitution is opt-in by name, so all three pass through untouched.
 *
 * Add a name here only for a fact that is genuinely live and genuinely repeated. The
 * tool corpus mentions no other: there is no "250 tools" or "125 guides" in any content
 * file or widget.
 */
export const TOOL_TEXT_FACTS = ["styleCount"] as const;
export type ToolTextFact = (typeof TOOL_TEXT_FACTS)[number];

/**
 * `{name}`, but not `^{sup}` or `_{sub}`.
 *
 * The inline grammar spends braces on superscript and subscript, so a placeholder
 * pattern that ignored the preceding character would eat `^{styleCount}` — and, worse,
 * would silently rewrite a legitimate `_{2}` if a fact were ever named `2`.
 */
export const TEXT_FACT = /(^|[^^_])\{(\w+)\}/g;

/** Every brace token in one string, in reading order. */
export function textFactsIn(value: string): string[] {
  return [...value.matchAll(TEXT_FACT)].map((m) => m[2]);
}

/** Resolve the named facts in one string; anything else keeps its braces. */
export function substituteTextFacts(
  value: string,
  values: Partial<Record<ToolTextFact, string>>
): string {
  return value.replace(TEXT_FACT, (whole, before: string, name: string) =>
    name in values ? `${before}${values[name as ToolTextFact]}` : whole
  );
}
